import { getConnectionInfo, updateConnectionInfo } from './localStorage';
import { generateGuid } from '../../shared/services/utils';
import CryptoJS from 'crypto-js';

export const generateSessionId = (username, password) => {
  const u = username.trim().toLowerCase(), p = password.trim();
  return CryptoJS.SHA256(u + ":" + p + ":42").toString().substring(0, 24);
};

export class SessionManager {
  /** Get saved sessions */
  static getSessions() {
    try {
      const s = localStorage.getItem('savedSessions');
      const sessions = s ? JSON.parse(s) : [];
      return sessions;
    } catch (e) {
      console.error('Failed to get sessions:', e);
      return [];
    }
  }

  /** Save session */
  static saveSession(session) {
    try {
      if (!session.id) throw new Error('Session ID is required');
      const sessions = this.getSessions(), i = sessions.findIndex(s => s.id === session.id);
      if (i >= 0) sessions[i] = { ...sessions[i], ...session }; else sessions.push(session);
      localStorage.setItem('savedSessions', JSON.stringify(sessions));
      return sessions;
    } catch (e) {
      console.error('Failed to save session:', e);
      return this.getSessions();
    }
  }

  /** Delete session */
  static deleteSession(id) {
    try {
      const s = this.getSessions().filter(x => x.id !== id);
      localStorage.setItem('savedSessions', JSON.stringify(s));
      return s;
    } catch (e) {
      console.error('Failed to delete session:', e);
      return this.getSessions();
    }
  }

  /** Switch session */
  static switchSession(id) {
    try {
      const c = getConnectionInfo(); updateConnectionInfo({ ...c, currentSessionId: id });
      window.location.reload();
    } catch (e) {
      console.error('Failed to switch session:', e);
    }
  }

  /** Get current session */
  static getCurrentSession() {
    const c = getConnectionInfo();
    if (!c.currentSessionId) return null;
    const s = this.getSessions();
    const cs = s.find(x => x.id === c.currentSessionId);
    if (!cs && c.currentSessionId) {
      return { id: c.currentSessionId, name: `Sesja ${c.currentSessionId.substring(0, 8)}`, dateCreated: new Date().toISOString() };
    }
    return cs || null;
  }

  /** Reader access requires previously entered credentials, not a bare shared ID. */
  static getCredentialSession() {
    const session = this.getCurrentSession();
    if (!session?.username?.trim() || !session?.password?.trim()) return null;
    return generateSessionId(session.username, session.password) === session.id ? session : null;
  }

  /** Auto-save current session */
  static saveCurrentSession(n = null) {
    const c = getConnectionInfo();
    if (!c.currentSessionId) return;
    const cs = this.getCurrentSession();
    if (cs) {
      this.saveSession({ ...cs, name: n || cs.name, lastAccessed: new Date().toISOString() });
    } else {
      this.saveSession({
        id: c.currentSessionId,
        name: n || `Sesja ${c.currentSessionId.substring(0, 8)}`,
        dateCreated: new Date().toISOString(),
        lastAccessed: new Date().toISOString()
      });
    }
  }

  static _buildSessionData(id, n, u, p) {
    const now = new Date().toISOString();
    return { id, name: n, username: u, password: p, dateCreated: now, lastAccessed: now };
  }

  /** Create new session */
  static createNewSession(name = null, username = null, password = null) {
    const sid = (username && password) ? generateSessionId(username, password) : generateGuid();
    const sn = name || `Sesja dla ${username || 'użytkownika'} (${sid.substring(0, 8)})`;
    this.saveSession(this._buildSessionData(sid, sn, username, password));
    return sid;
  }

  /** Login and create session */
  static login(username, password, sessionName = null) {
    if (!username || !password) throw new Error('Username and password are required');
    const sid = generateSessionId(username, password);
    const n = sessionName && sessionName.trim() ? sessionName.trim() : `Sesja dla ${username.trim()} (${sid.substring(0, 8)})`;
    const s = this._buildSessionData(sid, n, username.trim().toLowerCase(), password.trim());
    this.saveSession(s);
    const c = getConnectionInfo(); updateConnectionInfo({ ...c, currentSessionId: sid });
    return s;
  }
}